import { useCallback } from 'react';

import {
  alpha,
  Box,
  ButtonBase,
  Fade,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import MicIcon from '@material-ui/icons/Mic';
import PersonIcon from '@material-ui/icons/Person';
import VideocamOutlinedIcon from '@material-ui/icons/VideocamOutlined';
import Image from 'material-ui-image';
import ReactPlayer from 'react-player';

import { ReactComponent as LocationDefaultImage } from 'assets/images/location-default.svg';
import { AtendimentoInterface } from 'store/reducers/atendimentos';
import { ContatoInterface } from 'store/reducers/contatos';
import {
  MensagemCtaInterface,
  MensagemInterface,
  MensagemListaInterativaInterface,
  MensagemRespostaInterativaInterface,
  ReplyMensagemInterativaInterface,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';
import { ContatoMencionado } from 'types';
import { calculateMediaTime } from 'utils/calculateMediaTime';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';
import { getMentionColors } from 'utils/mentionColors';
import { renderTextWithMentions } from 'utils/renderTextWithMentions';

import ImageViewer from './ImageViewer';

interface ReplyMessagePreviewProps {
  replyMessage: MensagemInterface;
  atendimentoSelecionado: AtendimentoInterface | undefined;
  isGroup?: boolean;
  handleClearReplyMessage: () => void;
}

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    borderRadius: spacing(1),
    margin: spacing(1),
    backgroundColor: palette.background.default,
    borderLeft: `4px solid ${palette.primary.main}`,
    padding: spacing(1),
  },
  truncateText2Lines: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    '& a.mention': {
      borderRadius: 4,
      ...getMentionColors(palette),
      paddingInline: 4,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'inherit',
      pointerEvents: 'none',
    },
  },
  backgroundVideo: {
    backgroundColor: alpha(palette.common.black, 0.86),
  },
  backgroundImage: {
    height: 60,
    width: 60,
    borderRadius: spacing(0.5),
    backgroundColor: alpha(palette.common.black, 0.76),
  },
  rootBoxContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  rootBoxContentLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  image: {
    width: '100%',
    borderRadius: spacing(0.5),
  },
}));

function ReplyTypography({
  text,
  contatosMencionados,
}: {
  text: string;
  contatosMencionados?: ContatoMencionado[];
}) {
  const classes = useStyles();

  return (
    <Typography
      color="textSecondary"
      variant="body2"
      className={classes.truncateText2Lines}
      dangerouslySetInnerHTML={{
        __html: renderTextWithMentions(text, contatosMencionados || []),
      }}
    />
  );
}

function ReplyMessagePreview({
  replyMessage,
  isGroup = false,
  atendimentoSelecionado,
  handleClearReplyMessage,
}: ReplyMessagePreviewProps) {
  const classes = useStyles();
  const fromMe = replyMessage.autor !== 'CONTATO';

  function buildHeaderBodyText(headerText?: string, bodyText?: string) {
    if (headerText && bodyText) {
      return `${headerText}\n${bodyText}`;
    }

    return headerText || bodyText || '';
  }

  const mensagemContato = replyMessage.mensagemContato as ContatoInterface;
  const mensagemContatos = replyMessage.mensagemContato as ContatoInterface[];

  const renderReplyMessageContent = () => {
    const mediaDuration = replyMessage?.midiaDuracao || 0;
    const mediaTitle = replyMessage?.midiaTitulo;
    const mediaUrl = replyMessage?.midiaUrl;

    switch (replyMessage?.tipo) {
      case TiposMensagemEnum.WPP_REPLY_BTN_RESP:
      case TiposMensagemEnum.WPP_LIST_RESP:
      case TiposMensagemEnum.TEXTO: {
        const interactiveComponents =
          replyMessage.whatsappInteractiveComponents as ReplyMensagemInterativaInterface;

        const text =
          interactiveComponents?.list_reply?.title ||
          interactiveComponents?.button_reply?.title ||
          replyMessage.mensagem;

        return (
          <ReplyTypography
            text={text}
            contatosMencionados={replyMessage.contatosMencionados}
          />
        );
      }

      case TiposMensagemEnum.AUDIO:
        return (
          <Box className={classes.rootBoxContentLeft}>
            <MicIcon color="action" />
            <Typography color="textSecondary" variant="body2">
              Mensagem de voz ({calculateMediaTime(mediaDuration)})
            </Typography>
          </Box>
        );

      case TiposMensagemEnum.IMAGEM:
        return (
          <Box className={classes.rootBoxContent}>
            <Box className={classes.rootBoxContentLeft}>
              <ImageIcon color="action" />
              <ReplyTypography
                text={mediaTitle || 'Imagem'}
                contatosMencionados={replyMessage.contatosMencionados}
              />
            </Box>

            <Box className={classes.backgroundImage}>
              <ImageViewer src={mediaUrl}>
                <Image
                  cover
                  loading={false}
                  src={mediaUrl || ''}
                  className={classes.image}
                  style={{ backgroundColor: 'inherit' }}
                />
              </ImageViewer>
            </Box>
          </Box>
        );

      case TiposMensagemEnum.VIDEO:
        return (
          <Box className={classes.rootBoxContent}>
            <Box className={classes.rootBoxContentLeft}>
              <VideocamOutlinedIcon color="action" />

              <Typography color="textSecondary" variant="body2">
                {calculateMediaTime(mediaDuration)}
              </Typography>

              <ReplyTypography
                text={mediaTitle || 'Vídeo'}
                contatosMencionados={replyMessage.contatosMencionados}
              />
            </Box>

            <Box className={classes.backgroundVideo}>
              <ReactPlayer url={mediaUrl || ''} width={60} height={60} />
            </Box>
          </Box>
        );

      case TiposMensagemEnum.DOCUMENTO:
        return (
          <Box className={classes.rootBoxContentLeft}>
            <InsertDriveFileOutlinedIcon color="action" />

            <ReplyTypography
              text={mediaTitle || replyMessage?.midiaFileName || 'Documento'}
              contatosMencionados={replyMessage.contatosMencionados}
            />
          </Box>
        );

      case TiposMensagemEnum.LOCALIZACAO:
        return (
          <Box className={classes.rootBoxContent}>
            <Box className={classes.rootBoxContentLeft}>
              <LocationOnOutlinedIcon color="action" />

              <ReplyTypography text="Localização" />
            </Box>

            <LocationDefaultImage
              style={{ borderRadius: 4 }}
              width={60}
              height={60}
            />
          </Box>
        );

      case TiposMensagemEnum.CONTATO:
        return (
          <Box className={classes.rootBoxContentLeft}>
            <PersonIcon color="action" />

            <ReplyTypography
              text={`Contato: ${
                mensagemContato?.nome || mensagemContato?.contato
              }`}
            />
          </Box>
        );

      case TiposMensagemEnum.ARRAY_CONTATOS:
        return (
          <Box className={classes.rootBoxContentLeft}>
            <PersonIcon color="action" />

            <ReplyTypography text={`${mensagemContatos?.length} contatos`} />
          </Box>
        );

      case TiposMensagemEnum.TEMPLATE_WHATSAPP: {
        const components = replyMessage.whatsappTemplateData?.components;
        const header = components?.find(c => c.type === 'HEADER');
        const body = components?.find(c => c.type === 'BODY');

        return (
          <ReplyTypography
            text={buildHeaderBodyText(
              header?.text ? `*${header?.text}*` : undefined,
              body?.text,
            )}
            contatosMencionados={replyMessage.contatosMencionados}
          />
        );
      }

      case TiposMensagemEnum.WPP_CTA: {
        const wppCta =
          replyMessage.whatsappInteractiveComponents as MensagemCtaInterface;

        return (
          <ReplyTypography
            text={buildHeaderBodyText(
              wppCta.header?.text ? `*${wppCta.header?.text}*` : undefined,
              wppCta.body?.text,
            )}
            contatosMencionados={replyMessage.contatosMencionados}
          />
        );
      }

      case TiposMensagemEnum.WPP_LIST: {
        const wppList =
          replyMessage.whatsappInteractiveComponents as MensagemListaInterativaInterface;

        return (
          <ReplyTypography
            text={buildHeaderBodyText(wppList.header?.text, wppList.body?.text)}
            contatosMencionados={replyMessage.contatosMencionados}
          />
        );
      }

      case TiposMensagemEnum.WPP_REPLY_BTN: {
        const wppReplyBtn =
          replyMessage.whatsappInteractiveComponents as MensagemRespostaInterativaInterface;

        return (
          <ReplyTypography
            text={buildHeaderBodyText(
              wppReplyBtn.header?.text,
              wppReplyBtn.body?.text,
            )}
            contatosMencionados={replyMessage.contatosMencionados}
          />
        );
      }

      default:
        return null;
    }
  };

  const getAutor = useCallback((mensagem: MensagemInterface) => {
    const { autor, usuario } = mensagem;

    const msgUsuario = getUsuarioNomeExibicao(usuario);

    if (autor === 'BOT') return 'Bot';
    if (autor === 'ADMINISTRADOR') return `ADM: ${msgUsuario}`;
    if (autor === 'AUTO') return '';
    if (autor === 'ATENDENTE_EXTERNO') return '';

    return msgUsuario;
  }, []);

  return (
    <Fade in unmountOnExit mountOnEnter>
      <Box className={classes.root} style={{ maxWidth: '100%' }}>
        <Box style={{ flexGrow: 1 }}>
          <Typography
            color="primary"
            variant="body2"
            style={{ fontWeight: 500 }}
          >
            {isGroup && (
              <>
                {atendimentoSelecionado?.contato.nome ||
                  replyMessage?.contato?.nome ||
                  getAutor(replyMessage)}
              </>
            )}

            {!isGroup && (
              <>
                {fromMe
                  ? replyMessage?.contato?.nome || getAutor(replyMessage)
                  : atendimentoSelecionado?.contato.nome}
              </>
            )}
          </Typography>

          {renderReplyMessageContent()}
        </Box>

        <Box ml={1}>
          <ButtonBase onClick={handleClearReplyMessage}>
            <CloseOutlined color="action" />
          </ButtonBase>
        </Box>
      </Box>
    </Fade>
  );
}

export default ReplyMessagePreview;
